import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { PortableText } from "@/components/PortableText";
import NotFoundPage from "@/pages/NotFoundPage";
import { useSeo } from "@/lib/useSeo";
import { peek } from "@/lib/sanity";
import { getPerspectivePage, getPerspectivePosts, getPostBySlug } from "@/lib/sanityQueries";
import { formatDate, readingTime, firstSharedLine, imageUrl, postPath, hasPage, isLive } from "@/lib/perspective";

const SITE_URL = "https://easilinked.com";

// Article structured data, so search engines and AI answer engines can attribute it.
const useArticleJsonLd = (post) => {
    useEffect(() => {
        if (!post) return undefined;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "easilinked-article-jsonld";
        script.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.seo?.metaDescription || post.excerpt,
            datePublished: post.format === "fieldNote" ? post.originalSource?.originalPublishedAt || post.publishedAt : post.publishedAt,
            image: post.heroImage?.url ? [imageUrl(post.heroImage.url, 1600)] : undefined,
            author: post.author?.name ? { "@type": "Person", name: post.author.name } : { "@id": `${SITE_URL}/#organization` },
            publisher: { "@id": `${SITE_URL}/#organization` },
            mainEntityOfPage: `${SITE_URL}${postPath(post)}`,
        });
        document.head.appendChild(script);
        return () => script.remove();
    }, [post]);
};

const RelatedCard = ({ post }) => (
    <article className="flex h-full flex-col rounded-3xl border border-brand-line bg-brand-bg p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-accent">
            {post.format === "fieldNote" ? "Field Note" : "Article"} · {post.topic?.title}
        </p>
        <h3 className="mt-3 font-heading text-lg font-bold leading-snug tracking-tight text-brand-text">
            {hasPage(post) ? (
                <Link to={postPath(post)} className="hover:text-brand-blue">
                    {post.title}
                </Link>
            ) : (
                <Link to="/perspective" className="hover:text-brand-blue">
                    {post.title}
                </Link>
            )}
        </h3>
        <p className="mt-auto pt-4 text-sm text-brand-muted">
            {post.format === "fieldNote" ? firstSharedLine(post.originalSource) : readingTime(post.words)}
        </p>
    </article>
);

const PostContent = ({ post, page, related }) => {
    const isNote = post.format === "fieldNote";
    const cta = page.closingCta;
    const metaLine = isNote
        ? firstSharedLine(post.originalSource)
        : [formatDate(post.publishedAt), readingTime(post.words)].filter(Boolean).join(" · ");

    return (
        <>
            <article>
                <header className="relative overflow-hidden pt-36 pb-10 sm:pt-44">
                    <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                    <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
                        <MountReveal>
                            <Link
                                to="/perspective"
                                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-muted transition-colors duration-150 hover:text-brand-accent"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                                The EasiLinked Perspective
                            </Link>
                        </MountReveal>
                        <MountReveal delay={0.05}>
                            <span className="mt-6 inline-flex items-center">
                                <Overline>{isNote ? `Field Note · ${post.topic?.title || ""}` : post.topic?.title}</Overline>
                            </span>
                        </MountReveal>
                        <MountReveal delay={0.1}>
                            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.04] tracking-tight text-brand-text sm:text-5xl">
                                {post.title}
                            </h1>
                        </MountReveal>
                        {!isNote && post.excerpt && (
                            <MountReveal delay={0.15}>
                                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-muted">{post.excerpt}</p>
                            </MountReveal>
                        )}
                        <MountReveal delay={0.2}>
                            <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-brand-muted">
                                {post.author?.photo?.url && (
                                    <img
                                        src={imageUrl(post.author.photo.url, 96)}
                                        alt=""
                                        width="44"
                                        height="44"
                                        className="h-11 w-11 rounded-full object-cover"
                                    />
                                )}
                                <div className="flex flex-col">
                                    {post.author?.name && <span className="font-semibold text-brand-text">{post.author.name}</span>}
                                    <span>{metaLine}</span>
                                </div>
                            </div>
                        </MountReveal>
                    </div>
                </header>

                {post.heroImage?.url && (
                    <div className="mx-auto max-w-6xl px-5 sm:px-8">
                        <img
                            src={imageUrl(post.heroImage.url, 1800)}
                            alt={post.heroImage.alt || ""}
                            className="aspect-[16/9] w-full rounded-3xl border border-brand-line object-cover"
                        />
                    </div>
                )}

                <div className="mx-auto max-w-3xl px-5 py-14 text-[17px] leading-[1.75] text-brand-text/90 sm:px-8">
                    <PortableText value={post.body} />

                    {isNote && post.originalSource?.originalUrl && (
                        <p className="mt-10 text-sm text-brand-muted">
                            <a href={post.originalSource.originalUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
                                View the original post
                            </a>
                        </p>
                    )}

                    {post.author?.bio && (
                        <aside aria-label="About the author" className="mt-14 flex gap-5 border-t border-brand-line pt-8">
                            {post.author.photo?.url && (
                                <img
                                    src={imageUrl(post.author.photo.url, 160)}
                                    alt={post.author.photo.alt || ""}
                                    width="64"
                                    height="64"
                                    loading="lazy"
                                    className="h-16 w-16 shrink-0 rounded-full object-cover"
                                />
                            )}
                            <div className="text-[15px] leading-relaxed text-brand-muted">
                                <p className="font-semibold text-brand-text">{post.author.name}</p>
                                {post.author.role && <p className="text-sm">{post.author.role}</p>}
                                <p className="mt-2">{post.author.bio}</p>
                            </div>
                        </aside>
                    )}
                </div>
            </article>

            {related.length > 0 && (
                <section aria-labelledby="keep-reading" className="border-y border-brand-line bg-brand-surface py-20">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <h2 id="keep-reading" className="font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                            Keep reading
                        </h2>
                        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
                            {related.map((p) => (
                                <RelatedCard key={p._id} post={p} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {cta?.url && (
                <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
                    <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-16 text-center sm:px-16">
                        <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                        {page.closingHeadline && (
                            <h2 className="relative mx-auto max-w-3xl font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                                {page.closingHeadline}
                            </h2>
                        )}
                        {page.closingSupportingMessage && (
                            <p className="relative mx-auto mt-5 max-w-xl text-base leading-relaxed text-brand-muted">
                                {page.closingSupportingMessage}
                            </p>
                        )}
                        <div className="relative mt-9 flex justify-center">
                            <CtaLink href={cta.url} label={cta.label || "Schedule a Conversation"} openInNewTab={cta.openInNewTab} />
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

const PerspectivePostRoute = () => {
    const { slug } = useParams();
    const [page, setPage] = useState(() => peek(getPerspectivePage));
    const [post, setPost] = useState(() => peek(getPostBySlug, slug));
    const [all, setAll] = useState(() => peek(getPerspectivePosts) || []);
    const [loaded, setLoaded] = useState(() => peek(getPostBySlug, slug) !== null && peek(getPerspectivePage) !== null);

    useEffect(() => {
        let active = true;
        Promise.all([getPerspectivePage(), getPostBySlug(slug), getPerspectivePosts()])
            .then(([p, item, list]) => {
                if (!active) return;
                setPage(p);
                setPost(item);
                setAll(list || []);
                setLoaded(true);
            })
            .catch(() => active && setLoaded(true));
        return () => {
            active = false;
        };
    }, [slug]);

    const visible = isLive(page) && post && hasPage(post);
    useSeo(visible ? { metaTitle: `${post.title} | EasiLinked`, metaDescription: post.excerpt, ...post.seo } : undefined);
    useArticleJsonLd(visible ? post : null);

    if (!visible) {
        if (!loaded) return <PageLayout><section className="min-h-[60vh]" /></PageLayout>;
        return <NotFoundPage />;
    }

    // Chosen related posts first, otherwise the newest on the same topic.
    const chosen = (post.relatedPosts || []).filter(Boolean);
    const related = (chosen.length
        ? chosen
        : all.filter((p) => p._id !== post._id && p.topic?.slug === post.topic?.slug)
    ).slice(0, 3);

    return (
        <PageLayout>
            <PostContent post={post} page={page} related={related} />
        </PageLayout>
    );
};

export default PerspectivePostRoute;
